


























































import Vue from 'vue';
import {
  Component, Inject, Watch, Prop,
} from 'vue-property-decorator';
import { mapState } from 'vuex';
import { DataTableHeader } from 'vuetify';
import {
  Alert, NoticeClass, AuthStore, Dialog,
} from '@kasasa/fbase-components/lib';
import {
  KNoticeContainer,
} from '@kasasa/fbase-components';
import { Cycle } from '@/api/cycles';
import CycleSelect from '@/components/cycles/CycleSelect.vue';
import {
  QualOverride, QualOverrideResource, QualificationTypes, QualificationType,
} from '@/api/qualoverride';
import { Product, ProductResource } from '@/api/product';

interface QualOverrideTableEntry {
  [key: string]: any,
  id: string,
  productName: string,
  sortableOverriddenDate: number,
  overriddenDate: string,
  sortableQualRange: number,
  qualRange: string,
  qualOverrideType: string,
  numberOfAccountsOverridden: string,
  qualRewardLevel: number,
  overrideForAllAccounts: boolean
}

@Component({
  computed: mapState(['fiId']),
  components: { CycleSelect },
})
export default class QualificationOverrideManagerCard extends Vue {
  @Prop({ default: null }) lastSavedOverrideTime: Date | null = null;

  fiId!: string;

  selectedCycle: Cycle | null = null;

  loading: boolean = true;

  headers: DataTableHeader[] = [];

  items: QualOverrideTableEntry[] = [];

  @Inject('ProductResource')
  productResource!: ProductResource;

  @Inject('QualOverrideResource')
  qualOverrideResource!: QualOverrideResource;

  products: Product[] = [];


  mounted() {
    this.loadData();
  }

  @Watch('selectedCycle')
  async loadData() {
    if (this.selectedCycle) {
      this.buildHeaders();
      this.loading = true;
      await this.loadProducts();
      await this.loadOverrides();
    }
  }

  buildHeaders() {
    this.headers = [];
    this.headers.push({ text: 'Date Overridden', value: 'sortableOverriddenDate' });
    this.headers.push({ text: 'Product', value: 'productName' });
    this.headers.push({ text: 'Qualification Range', value: 'sortableQualRange' });
    this.headers.push({ text: 'Override Type', value: 'qualOverrideType' });
    this.headers.push({ text: 'Number of Accounts Overridden', value: 'numberOfAccountsOverridden' });
    this.headers.push({ text: 'Level', value: 'qualRewardLevel' });

    if (!this.isFiUser()) {
      this.headers.push({ text: 'Cancel Override', value: 'cancel', sortable: false });
    }
  }

  isFiUser(): boolean {
    return this.$store.state.isFiUser;
  }

  async loadProducts() {
    this.products = [];
    await this.productResource.getAllProducts(this.fiId)
      .then((results: Product[]) => {
        this.products = results;
      }).catch((error: any) => {
        this.showErrorMessage(error);
      });
  }

  @Watch('lastSavedOverrideTime')
  async loadOverrides() {
    this.loading = true;
    const newItems: QualOverrideTableEntry[] = [];
    await this.qualOverrideResource.getOverrides(this.fiId)
      .then((results: QualOverride[]) => {
        results.forEach((override: QualOverride) => {
          if (this.selectedCycle
            && new Date(override.startDate) <= new Date(this.selectedCycle.qualificationEndDate)
            && new Date(override.endDate) >= new Date(this.selectedCycle.qualificationStartDate)) {
            newItems.push({
              id: override.id,
              productName: this.getProductNameFromExternalProductMappingId(override.externalProductMappingId),
              sortableOverriddenDate: new Date(override.overrideDate).getTime(),
              overriddenDate: new Date(override.overrideDate).toLocaleDateString('en-US', {
                timeZone: 'UTC',
              }),
              sortableQualRange: new Date(override.startDate).getTime(),
              qualRange: `${new Date(override.startDate).toLocaleDateString('en-US', {
                timeZone: 'UTC',
              })} - ${new Date(override.endDate).toLocaleDateString('en-US', {
                timeZone: 'UTC',
              })}`,
              qualOverrideType: QualificationOverrideManagerCard.getOverrideTypeText(override),
              numberOfAccountsOverridden: override.overrideForAllAccounts ? 'All Accounts' : `${override.accountKasasaKeys.length}`,
              qualRewardLevel: override.qualRewardLevel,
              overrideForAllAccounts: override.overrideForAllAccounts,
            });
          }
        });
        this.items = newItems;
      }).catch((error: any) => {
        this.showErrorMessage(error);
      }).finally(() => {
        this.loading = false;
      });
  }

  static getOverrideTypeText(override: QualOverride) {
    let overrideType: string = '?';
    if (override.qualOverrideType === 'OVERALL') {
      overrideType = 'Overall Qualification';
    } else if (QualificationTypes.has(override.qualOverrideType)) {
      const qualType: QualificationType | undefined = QualificationTypes.get(override.qualOverrideType);
      if (qualType) {
        overrideType = qualType.description;
      }
    }
    return overrideType;
  }

  getProductNameFromExternalProductMappingId(externalProductMappingId: number): string {
    let productName = '?';
    for (let i = 0; i < this.products.length; i++) {
      if (this.products[i].productMapping.externalProductMappingId === externalProductMappingId) {
        productName = this.products[i].productMapping.fiProductName;
        break;
      }
    }
    return productName;
  }

  getTableValue = (item: QualOverrideTableEntry, header: DataTableHeader) => {
    if (header.value === 'sortableOverriddenDate') {
      return item.overriddenDate;
    } if (header.value === 'sortableQualRange') {
      return item.qualRange;
    }
    return item[header.value];
  }

  onDelete(item: QualOverrideTableEntry) {
    const accounts: string = item.overrideForAllAccounts ? 'all' : item.numberOfAccountsOverridden;
    const dialog = new Dialog('Confirm', `Are you sure you want to delete a qualifier override for ${item.productName} of type '${item.qualOverrideType}' for ${accounts} accounts?`);
    dialog.setAcceptLabel('OK')
      .setDeclineLabel('Cancel')
      .setDismissable(false);

    this.$store.dispatch('notices/add', dialog).then((ok) => {
      if (ok === 'accept') {
        this.deleteOverride(item);
      }
    });
  }

  async deleteOverride(item: QualOverrideTableEntry) {
    this.loading = true;

    await this.qualOverrideResource.deleteQualOverrideById(this.fiId, item.id)
      .then(() => {
        this.loadOverrides();
      }).catch((error: any) => {
        this.showErrorMessage(error);
      }).finally(() => {
        this.loading = false;
      });
  }

  downloadReport() {
    if (this.selectedCycle) {
      window.location.href = `/api/rcx/reporting/${this.fiId}/qual-override-report/${this.selectedCycle.id}`;
    }
  }

  showErrorMessage(error: string) {
    const al = new Alert(error, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }
}
